import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from './button'
import { ArrowUpRight } from 'lucide-react'

export const CurrentLocation = () => {
    const [permissionState, setPermissionState] = useState<PermissionState>()
    const [loading, setLoading] = useState(false)
    const [position, setPosition] = useState<GeolocationPosition>()
    const [denied, setDenied] = useState(false)
  
    const { accuracy, latitude, longitude } = useMemo(() => {
      const accuracy = position?.coords?.accuracy ?? 0
      let latitude = (position?.coords?.latitude ?? 0).toFixed(4)
      let longitude = (position?.coords?.longitude ?? 0).toFixed(4)
  
      latitude =
        latitude.substring(0, latitude.indexOf('.') + 3) +
        latitude.substring(latitude.indexOf('.') + 3)
      longitude =
        longitude.substring(0, longitude.indexOf('.') + 3) +
        longitude.substring(longitude.indexOf('.') + 3)
  
      return {
        accuracy: Math.round(accuracy),
        latitude: latitude.replace('.', '. '),
        longitude: longitude.replace('.', '. '),
      }
    }, [position])
  
    const requestLocation = useCallback(() => {
      setLoading(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setPosition(position)
          setLoading(false)
        },
        (error) => {
          console.error(error)
          if (error?.code === 1) {
            setDenied(true)
          }
          setLoading(false)
        }
      )
    }, [])
  
    useEffect(() => {
      if (permissionState === 'granted') {
        requestLocation()
      }
    }, [permissionState, requestLocation])
  
    useEffect(() => {
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then((result) => {
            if (result.state === 'denied') {
              setDenied(true)
            }
            setPermissionState(result.state)
            result.onchange = () => {
              setPermissionState(result.state)
              if (result.state === 'denied') {
                setDenied(true)
              }
            }
          })
          .catch((error) => {
            console.log('error', error)
          })
      }
    }, [])

    const onSend = useCallback(() => {
      const body = `My current position is:
      
                    Lat: ${position?.coords?.latitude}
                    Lon: ${position?.coords?.longitude}
                    
                    Margin of error: ${accuracy} m
                    Coordinate system: WGS84
                    
                    Powered by Twiceme
                    
                    http://maps.google.com/maps?q=loc:${position?.coords?.latitude},${position?.coords?.longitude}`
                    // https://www.google.com/maps/search/?api=1&query=${position?.coords?.latitude},${position?.coords?.longitude}`
  
      const link = document.createElement('a')
      link.href = 'sms:?&body=' + encodeURIComponent(body)
      link.click()
      link.remove()
      // window.open('sms:?&body=' + encodeURIComponent(body), '_self')
    }, [position, accuracy])

    return (
        <div>
            {!position && permissionState !== 'denied' && !denied && !loading && (
                <div className="border border-primary-black rounded-2xl p-3 flex flex-col gap-4">
                    <div className='flex flex-row justify-between gap-2 items-center'>
                        <p>To enable this feature, allow location access</p>
                        <Button variant='secondary' className='w-28 h-12 items-center rounded-2xl' onClick={requestLocation}>Allow</Button>
                    </div>
                    <div className="flex flex-row justify-between items-center">
                        <div className='flex flex-col'>
                            <p className="text-text-disabled">Current coordinates</p>
                            <p className="text-text-disabled">0° N, 0° E</p>
                        </div>
                        <Button disabled className='w-28 h-12 items-center rounded-2xl'>
                            <p>Send</p>
                            <ArrowUpRight/>
                        </Button>
                    </div>
                </div>
            )}
            {
                (permissionState === 'denied' || denied) && (
                    <div className="border border-primary-black rounded-2xl p-2">
                        <p>Location services are disabled. Enable in your browser to view and share your location.</p>
                    </div>
                )
            }
            {position && !loading && (
                <div className="flex flex-row justify-between">
                    <div className="flex flex-col">
                        <p>Current coordinates </p>
                        <p className='font-bold'>{longitude}° N, {latitude}° E</p>
                    </div>
                    <Button 
                      variant='secondary' 
                      className='max-w-28 h-12 items-center rounded-2xl' 
                      onClick={onSend}>
                        <p>Send</p>
                        <ArrowUpRight/>
                    </Button>
                </div>
            )}
        </div>
    )
}
